import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { eur, CHANTIER_TYPES, LIBRARY_ITEM_TYPES, MATERIAL_TYPES, ACCESSOIRES, TVA_RATES, MEMORIZE_CATEGORIES, guessCategory } from "@/lib/constants";
import { generatePdf } from "@/lib/pdf";
import LibraryPicker from "@/components/LibraryPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Plus, Trash2, Library, Save, FileDown, ReceiptEuro } from "lucide-react";

const emptyLine = { designation: "", description: "", prestation_type: "matériel", quantity: 1, unit: "U", unit_price_ht: 0, tva_rate: 0, brand: "", model: "", library_id: null };
const emptyEquipment = { material_type: "", brand_model: "", power: "", accessoires: [] };

export default function DevisEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [clients, setClients] = useState([]);
  const [settings, setSettings] = useState({});
  const [devis, setDevis] = useState({
    client_id: "",
    chantier_address: "",
    date: new Date().toISOString().slice(0, 10),
    status: "brouillon",
    chantier_type: "Installation climatisation",
    chantier_type_other: "",
    equipment: { ...emptyEquipment },
    lines: [{ ...emptyLine }],
    discount_type: "percent",
    discount_value: 0,
    notes: "",
  });
  const [loaded, setLoaded] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [newLinePrompt, setNewLinePrompt] = useState(null);   // {lines:[{...,_cat}]}
  const [pricePrompt, setPricePrompt] = useState(null);       // {changes:[{item, newPrice}]}

  const defaultTva = () => Number(settings.default_tva_rate ?? 0);

  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data));
    api.get("/settings").then((r) => setSettings(r.data));
  }, []);

  useEffect(() => {
    if (isEdit) {
      api.get(`/devis/${id}`).then((r) => {
        setDevis({
          ...r.data,
          equipment: r.data.equipment || { ...emptyEquipment },
          chantier_type_other: r.data.chantier_type_other || "",
          discount_type: r.data.discount_type || "percent",
          discount_value: r.data.discount_value ?? 0,
        });
        setLoaded(true);
      }).catch(() => { toast.error("Devis introuvable"); navigate("/devis"); });
    } else {
      setLoaded(true);
    }
  }, [id, isEdit, navigate]);

  // Sync pristine blank lines to the default TVA once settings resolve (new devis only)
  useEffect(() => {
    if (isEdit) return;
    const rate = Number(settings.default_tva_rate ?? 0);
    setDevis((d) => ({
      ...d,
      lines: d.lines.map((l) => (!l.library_id && !l.designation && Number(l.unit_price_ht) === 0) ? { ...l, tva_rate: rate } : l),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.default_tva_rate, isEdit]);

  const set = (k, v) => setDevis((d) => ({ ...d, [k]: v }));

  const onClientChange = (clientId) => {
    const c = clients.find((x) => x.id === clientId);
    setDevis((d) => ({ ...d, client_id: clientId, chantier_address: d.chantier_address || c?.address || "" }));
  };

  const updateLine = (i, k, v) =>
    setDevis((d) => {
      const lines = [...d.lines];
      lines[i] = { ...lines[i], [k]: v };
      return { ...d, lines };
    });

  const addLine = () =>
    setDevis((d) => ({ ...d, lines: [...d.lines, { ...emptyLine, tva_rate: defaultTva() }] }));

  const addFromLibrary = (item) => {
    if (item?.id) api.post(`/library/${item.id}/use`).catch(() => {});
    let price = Number(item.sale_price || 0);
    // "Sur devis" : demander le prix au moment de l'ajout
    if (item.pricing_mode === "sur_devis") {
      const entered = window.prompt(`Prix HT pour « ${item.designation} » (prestation sur devis) :`, "");
      if (entered === null) return;
      price = Number(String(entered).replace(",", ".")) || 0;
    }
    const modeSuffix = item.pricing_mode === "a_partir_de" ? " (à partir de)" : item.pricing_mode === "minimum" ? " (minimum)" : "";
    setDevis((d) => ({
      ...d,
      lines: [...d.lines, {
        designation: (item.reference ? `[${item.reference}] ` : "") + (item.designation || "") + modeSuffix,
        description: item.description || "",
        prestation_type: item.item_type || "matériel",
        quantity: 1,
        unit: item.unit || "U",
        unit_price_ht: price,
        tva_rate: Number(item.tva_rate ?? defaultTva()),
        brand: item.brand || "",
        model: item.model || "",
        library_id: item.id,
      }],
    }));
    toast.success(`Ligne ajoutée : ${item.designation}`);
  };

  const addPack = async (pack) => {
    const items = pack.items || [];
    if (items.length === 0) { toast.error("Ce pack est vide"); return; }
    setDevis((d) => ({
      ...d,
      lines: [
        ...d.lines.filter((l) => l.designation || l.library_id || Number(l.unit_price_ht) > 0),
        ...items.map((it) => ({
          designation: it.designation || "",
          description: it.description || "",
          prestation_type: it.prestation_type || "matériel",
          quantity: Number(it.quantity || 1),
          unit: it.unit || "U",
          unit_price_ht: Number(it.unit_price_ht || 0),
          tva_rate: Number(it.tva_rate ?? defaultTva()),
          brand: it.brand || "",
          model: it.model || "",
          library_id: it.library_id || null,
        })),
      ],
    }));
    toast.success(`Pack ajouté : ${pack.name} (${items.length} lignes)`);
  };

  const removeLine = (i) => setDevis((d) => ({ ...d, lines: d.lines.filter((_, idx) => idx !== i) }));

  const setEquip = (k, v) => setDevis((d) => ({ ...d, equipment: { ...d.equipment, [k]: v } }));
  const toggleAccessoire = (a) =>
    setDevis((d) => {
      const cur = d.equipment.accessoires || [];
      const next = cur.includes(a) ? cur.filter((x) => x !== a) : [...cur, a];
      return { ...d, equipment: { ...d.equipment, accessoires: next } };
    });

  const setDiscount = (k, v) => setDevis((d) => ({ ...d, [k]: v }));

  const totals = useMemo(() => {
    let subtotal = 0;
    devis.lines.forEach((l) => { subtotal += Number(l.quantity || 0) * Number(l.unit_price_ht || 0); });
    const dv = Number(devis.discount_value || 0);
    let discount = devis.discount_type === "percent"
      ? subtotal * Math.max(0, Math.min(dv, 100)) / 100
      : Math.max(0, Math.min(dv, subtotal));
    const ht = subtotal - discount;
    const ratio = subtotal > 0 ? ht / subtotal : 1;
    let tva = 0;
    devis.lines.forEach((l) => {
      const lht = Number(l.quantity || 0) * Number(l.unit_price_ht || 0) * ratio;
      tva += (lht * Number(l.tva_rate || 0)) / 100;
    });
    return { subtotal, discount, ht, tva, ttc: ht + tva };
  }, [devis.lines, devis.discount_type, devis.discount_value]);

  const buildPayload = () => ({
    client_id: devis.client_id,
    chantier_address: devis.chantier_address,
    date: devis.date,
    status: devis.status,
    chantier_type: devis.chantier_type,
    chantier_type_other: devis.chantier_type === "Autre" ? (devis.chantier_type_other || "") : "",
    equipment: devis.equipment,
    discount_type: devis.discount_type,
    discount_value: Number(devis.discount_value || 0),
    notes: devis.notes,
    lines: devis.lines
      .filter((l) => (l.designation && l.designation.trim()) || Number(l.unit_price_ht) > 0)
      .map((l) => ({
      designation: l.designation,
      description: l.description || "",
      prestation_type: l.prestation_type,
      quantity: Number(l.quantity || 0),
      unit: l.unit || "U",
      unit_price_ht: Number(l.unit_price_ht || 0),
      tva_rate: Number(l.tva_rate || 0),
      brand: l.brand || "",
      model: l.model || "",
      library_id: l.library_id || null,
    })),
  });

  const detectNewLines = () =>
    devis.lines.filter((l) => l.designation && l.designation.trim() && !l.library_id && Number(l.unit_price_ht) > 0);

  // Compare library-based lines vs their current library sale price
  const detectPriceChanges = async () => {
    const libLines = devis.lines.filter((l) => l.library_id && Number(l.unit_price_ht) > 0);
    if (libLines.length === 0) return [];
    try {
      const { data } = await api.get("/library");
      const map = {};
      data.forEach((it) => { map[it.id] = it; });
      const changes = [];
      const seen = new Set();
      libLines.forEach((l) => {
        const item = map[l.library_id];
        if (item && !seen.has(l.library_id) && Math.abs(Number(item.sale_price) - Number(l.unit_price_ht)) > 0.001) {
          seen.add(l.library_id);
          changes.push({ item, newPrice: Number(l.unit_price_ht) });
        }
      });
      return changes;
    } catch { return []; }
  };

  const save = useCallback(async (silent = false, promptLibrary = false) => {
    if (!devis.client_id) { toast.error("Sélectionnez un client"); return null; }
    try {
      const payload = buildPayload();
      const res = isEdit ? await api.put(`/devis/${id}`, payload) : await api.post("/devis", payload);
      if (!silent) toast.success(isEdit ? "Devis enregistré" : `Devis créé : ${res.data.number}`);
      if (promptLibrary) {
        const changes = await detectPriceChanges();
        const newLines = detectNewLines().map((l) => ({ ...l, _cat: guessCategory(l.designation) }));
        if (changes.length > 0) setPricePrompt({ changes, pendingNewLines: newLines });
        else if (newLines.length > 0) setNewLinePrompt({ lines: newLines });
      }
      if (!isEdit) navigate(`/devis/${res.data.id}`, { replace: true });
      else setDevis({ ...res.data, equipment: res.data.equipment || { ...emptyEquipment } });
      return res.data;
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devis, id, isEdit, navigate]);

  const applyPriceUpdate = async (doUpdate) => {
    const prompt = pricePrompt;
    setPricePrompt(null);
    if (doUpdate && prompt?.changes) {
      try {
        await Promise.all(prompt.changes.map(({ item, newPrice }) => {
          const { id: iid, marge, price_history, usage_count, last_used_at, created_at, search_blob, ...rest } = item;
          return api.put(`/library/${iid}`, { ...rest, sale_price: newPrice });
        }));
        toast.success("Prix habituel mis à jour dans la bibliothèque");
      } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    }
    if (prompt?.pendingNewLines?.length > 0) setNewLinePrompt({ lines: prompt.pendingNewLines });
  };

  const setPromptCat = (idx, cat) =>
    setNewLinePrompt((p) => ({ ...p, lines: p.lines.map((l, i) => i === idx ? { ...l, _cat: cat } : l) }));

  const saveNewLinesToLibrary = async () => {
    const lines = newLinePrompt?.lines || [];
    try {
      await Promise.all(lines.map((l) => api.post("/library", {
        category: l._cat || "Services", subcategory: "", designation: l.designation, description: l.description || "",
        brand: l.brand || "", model: l.model || "", unit: l.unit || "U",
        purchase_price: 0, sale_price: Number(l.unit_price_ht || 0),
        item_type: l.prestation_type || "matériel", tva_rate: Number(l.tva_rate || 0),
        favorite: false, active: true,
      })));
      toast.success(`${lines.length} ligne(s) enregistrée(s) dans la bibliothèque`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
    setNewLinePrompt(null);
  };

  const doPdf = async () => {
    const saved = await save(true);
    if (saved) generatePdf(saved, settings, "devis");
  };

  const doConvert = async () => {
    const saved = await save(true);
    if (!saved) return;
    try {
      const { data } = await api.post(`/devis/${saved.id}/convert`);
      toast.success("Brouillon de facture créé");
      navigate(`/factures/${data.id}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (!loaded) return <div className="p-8 text-slate-500">Chargement…</div>;

  return (
    <div className="p-4 md:p-8 space-y-6 max-w-5xl mx-auto">
      <button data-testid="back-button" onClick={() => navigate("/devis")} className="flex items-center gap-2 text-slate-600 font-semibold hover:text-slate-900">
        <ArrowLeft size={20} /> Retour aux devis
      </button>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900">
            {isEdit ? devis.number : "Nouveau devis"}
          </h1>
          {isEdit && devis.facture_id && <p className="text-[#16A34A] font-semibold">Ce devis a été facturé</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          {isEdit && (
            <>
              <Button variant="outline" data-testid="devis-pdf-button" onClick={doPdf} className="h-12 border-slate-300 font-bold"><FileDown size={18} className="mr-1" />PDF</Button>
              {!devis.facture_id && (
                <Button data-testid="devis-convert-button" onClick={doConvert} className="h-12 bg-[#0284C7] hover:bg-[#0369A1] font-bold"><ReceiptEuro size={18} className="mr-1" />Facturer</Button>
              )}
            </>
          )}
          <Button data-testid="devis-save-button" onClick={() => save(false, true)} className="h-12 bg-[#16A34A] hover:bg-[#15803D] font-bold"><Save size={18} className="mr-1" />Enregistrer</Button>
        </div>
      </div>

      {/* Meta */}
      <div className="bg-white border border-slate-200 rounded-md p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Client *</Label>
          <Select value={devis.client_id} onValueChange={onClientChange}>
            <SelectTrigger data-testid="devis-client-select" className="h-12"><SelectValue placeholder="Sélectionner un client" /></SelectTrigger>
            <SelectContent>
              {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {clients.length === 0 && <p className="text-xs text-red-500">Créez d'abord un client.</p>}
        </div>
        <div className="space-y-2">
          <Label>Type de chantier</Label>
          <Select value={devis.chantier_type} onValueChange={(v) => set("chantier_type", v)}>
            <SelectTrigger data-testid="devis-chantier-type" className="h-12"><SelectValue /></SelectTrigger>
            <SelectContent>{CHANTIER_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
          {devis.chantier_type === "Autre" && (
            <div className="pt-2 space-y-1">
              <Label>Précisez le type de chantier</Label>
              <Input
                data-testid="devis-chantier-type-other"
                className="h-12"
                placeholder="Ex : Pose de paroi de douche, travaux électriques…"
                value={devis.chantier_type_other || ""}
                onChange={(e) => set("chantier_type_other", e.target.value)}
              />
            </div>
          )}
        </div>
        <div className="space-y-2 md:col-span-2">
          <Label>Adresse chantier</Label>
          <Input data-testid="devis-address-input" className="h-12" value={devis.chantier_address} onChange={(e) => set("chantier_address", e.target.value)} />
        </div>
        <div className="space-y-2">
          <Label>Date</Label>
          <Input type="date" data-testid="devis-date-input" className="h-12" value={devis.date} onChange={(e) => set("date", e.target.value)} />
        </div>
        <div className="space-y-2">
          <Label>Statut</Label>
          <Select value={devis.status} onValueChange={(v) => set("status", v)}>
            <SelectTrigger data-testid="devis-status-select" className="h-12"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="brouillon">Brouillon</SelectItem>
              <SelectItem value="envoye">Envoyé</SelectItem>
              <SelectItem value="accepte">Accepté</SelectItem>
              <SelectItem value="refuse">Refusé</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Matériel clim / chauffage */}
      <div className="bg-white border border-slate-200 rounded-md p-6 space-y-4">
        <div className="flex items-center gap-2">
          <h2 className="font-heading text-xl font-bold">Matériel installé</h2>
          <span className="text-xs font-semibold text-[#16A34A]">Détails clim / chauffage</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>Type de matériel</Label>
            <Select value={devis.equipment.material_type || undefined} onValueChange={(v) => setEquip("material_type", v)}>
              <SelectTrigger data-testid="equip-material-type" className="h-12"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
              <SelectContent>{MATERIAL_TYPES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Marque / Modèle</Label>
            <Input data-testid="equip-brand-model" className="h-12" placeholder="Ex : Daikin Perfera" value={devis.equipment.brand_model} onChange={(e) => setEquip("brand_model", e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Puissance (kW / BTU)</Label>
            <Input data-testid="equip-power" className="h-12" placeholder="Ex : 3,5 kW / 12000 BTU" value={devis.equipment.power} onChange={(e) => setEquip("power", e.target.value)} />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Accessoires</Label>
          <div className="flex flex-wrap gap-3">
            {ACCESSOIRES.map((a) => {
              const checked = (devis.equipment.accessoires || []).includes(a);
              return (
                <label key={a} data-testid={`equip-accessoire-${a}`} className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer text-sm font-medium ${checked ? "border-[#16A34A] bg-green-50 text-[#15803D]" : "border-slate-200 text-slate-700"}`}>
                  <Checkbox checked={checked} onCheckedChange={() => toggleAccessoire(a)} />
                  {a}
                </label>
              );
            })}
          </div>
        </div>
      </div>

      {/* Lines */}
      <div className="bg-white border border-slate-200 rounded-md p-4 md:p-6 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h2 className="font-heading text-xl font-bold">Lignes de prestation</h2>
          <Button variant="outline" data-testid="open-library-picker" onClick={() => setPickerOpen(true)} className="h-11 border-[#0284C7] text-[#0284C7] font-bold"><Library size={18} className="mr-1" />Bibliothèque</Button>
        </div>

        <div className="space-y-3">
          {devis.lines.map((l, i) => {
            const lineHt = Number(l.quantity || 0) * Number(l.unit_price_ht || 0);
            return (
              <div key={i} data-testid={`devis-line-${i}`} className="border border-slate-200 rounded-md p-3 grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                <div className="md:col-span-4 space-y-1">
                  <Label className="text-xs">Désignation</Label>
                  <Input data-testid={`line-designation-${i}`} value={l.designation} onChange={(e) => updateLine(i, "designation", e.target.value)} className="h-11" />
                </div>
                <div className="md:col-span-2 space-y-1">
                  <Label className="text-xs">Type</Label>
                  <Select value={l.prestation_type || undefined} onValueChange={(v) => updateLine(i, "prestation_type", v)}>
                    <SelectTrigger data-testid={`line-type-${i}`} className="h-11"><SelectValue placeholder="Type" /></SelectTrigger>
                    <SelectContent>
                      {(LIBRARY_ITEM_TYPES.includes(l.prestation_type) || !l.prestation_type
                        ? LIBRARY_ITEM_TYPES
                        : [...LIBRARY_ITEM_TYPES, l.prestation_type]
                      ).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-1 space-y-1">
                  <Label className="text-xs">Qté</Label>
                  <Input type="number" step="0.5" data-testid={`line-qty-${i}`} value={l.quantity} onChange={(e) => updateLine(i, "quantity", e.target.value)} className="h-11" />
                </div>
                <div className="md:col-span-2 space-y-1">
                  <Label className="text-xs">P.U. HT {l.unit ? `(/${l.unit})` : ""}</Label>
                  <Input type="number" step="0.01" data-testid={`line-price-${i}`} value={l.unit_price_ht} onChange={(e) => updateLine(i, "unit_price_ht", e.target.value)} className="h-11" />
                </div>
                <div className="md:col-span-2 space-y-1">
                  <Label className="text-xs">TVA</Label>
                  <Select value={String(l.tva_rate)} onValueChange={(v) => updateLine(i, "tva_rate", Number(v))}>
                    <SelectTrigger data-testid={`line-tva-${i}`} className="h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TVA_RATES.map((t) => <SelectItem key={t.value} value={String(t.value)}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-1 flex items-center justify-between md:justify-end gap-2">
                  <span data-testid={`line-total-${i}`} className="font-bold text-slate-900 md:hidden">{eur(lineHt)}</span>
                  <Button size="icon" variant="ghost" data-testid={`remove-line-${i}`} onClick={() => removeLine(i)} className="text-red-600 h-11 w-11"><Trash2 size={18} /></Button>
                </div>
                <div className="hidden md:flex md:col-span-12 justify-between text-sm text-slate-500">
                  <span>{l.library_id ? "★ depuis bibliothèque" : (l.designation ? "ligne libre" : "")}</span>
                  <span>Total ligne HT : <span className="font-bold text-slate-900">{eur(lineHt)}</span></span>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <Button variant="outline" data-testid="open-library-picker-2" onClick={() => setPickerOpen(true)} className="h-11 flex-1 border-[#0284C7] text-[#0284C7] font-bold"><Library size={18} className="mr-1" />Ajouter depuis la bibliothèque</Button>
          <Button variant="outline" data-testid="add-line-button" onClick={addLine} className="h-11 flex-1 border-dashed border-slate-300 font-semibold"><Plus size={18} className="mr-1" />Ligne vide</Button>
        </div>
      </div>

      {/* Totals + notes */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-md p-6 space-y-2">
          <Label>Notes</Label>
          <Textarea data-testid="devis-notes-input" value={devis.notes} onChange={(e) => set("notes", e.target.value)} rows={5} />
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-6 space-y-3">
          <div className="flex justify-between text-slate-600"><span>Sous-total HT</span><span data-testid="subtotal-ht" className="font-semibold text-slate-900">{eur(totals.subtotal)}</span></div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-slate-600 shrink-0">Remise</span>
            <div className="flex items-center gap-2">
              <Input type="number" step="0.01" min="0" data-testid="discount-value" value={devis.discount_value} onChange={(e) => setDiscount("discount_value", e.target.value)} className="h-9 w-24 text-right" />
              <Select value={devis.discount_type} onValueChange={(v) => setDiscount("discount_type", v)}>
                <SelectTrigger data-testid="discount-type" className="h-9 w-20"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="percent">%</SelectItem>
                  <SelectItem value="amount">€</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {totals.discount > 0 && (
            <div className="flex justify-between text-[#16A34A]"><span>Remise appliquée</span><span data-testid="discount-amount" className="font-semibold">- {eur(totals.discount)}</span></div>
          )}
          <div className="flex justify-between text-slate-600 border-t border-slate-100 pt-2"><span>Total HT</span><span data-testid="total-ht" className="font-semibold text-slate-900">{eur(totals.ht)}</span></div>
          <div className="flex justify-between text-slate-600"><span>{totals.tva === 0 ? "TVA non applicable" : "TVA"}</span><span data-testid="total-tva" className="font-semibold text-slate-900">{eur(totals.tva)}</span></div>
          <div className="border-t border-[#16A34A] pt-3 flex justify-between items-center">
            <span className="font-heading font-bold text-lg">Total TTC</span>
            <span data-testid="total-ttc" className="font-heading font-extrabold text-2xl text-[#16A34A]">{eur(totals.ttc)}</span>
          </div>
        </div>
      </div>

      <LibraryPicker open={pickerOpen} onOpenChange={setPickerOpen} onPick={addFromLibrary} onCreateBlank={addLine} onPickPack={addPack} />

      {/* Prix habituel prompt */}
      <AlertDialog open={!!pricePrompt} onOpenChange={(o) => { if (!o) applyPriceUpdate(false); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mettre à jour le prix habituel ?</AlertDialogTitle>
            <AlertDialogDescription>
              Le prix de {pricePrompt?.changes?.length === 1 ? "cette prestation a été modifié" : "ces prestations a été modifié"} par rapport à la bibliothèque. Mettre à jour le prix habituel ?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="max-h-48 overflow-y-auto space-y-1 text-sm text-slate-700">
            {(pricePrompt?.changes || []).map((c, i) => (
              <div key={i} className="flex justify-between border-b border-slate-100 py-1 gap-2">
                <span className="truncate">{c.item.designation}</span>
                <span className="shrink-0"><span className="text-slate-400 line-through mr-2">{eur(c.item.sale_price)}</span><span className="font-semibold text-[#16A34A]">{eur(c.newPrice)}</span></span>
              </div>
            ))}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="price-keep">Non, garder ce prix uniquement ici</AlertDialogCancel>
            <AlertDialogAction data-testid="price-update" onClick={() => applyPriceUpdate(true)} className="bg-[#0284C7] hover:bg-[#0369A1]">Oui, mettre à jour</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!newLinePrompt} onOpenChange={(o) => !o && setNewLinePrompt(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Enregistrer dans la bibliothèque ?</AlertDialogTitle>
            <AlertDialogDescription>
              {newLinePrompt?.lines?.length === 1
                ? "Cette ligne semble nouvelle. Choisissez une catégorie puis enregistrez-la pour la réutiliser."
                : `${newLinePrompt?.lines?.length || 0} lignes semblent nouvelles. Vérifiez les catégories puis enregistrez-les.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="max-h-56 overflow-y-auto space-y-2 text-sm text-slate-700">
            {(newLinePrompt?.lines || []).map((l, i) => (
              <div key={i} className="flex items-center justify-between border-b border-slate-100 py-2 gap-2">
                <div className="min-w-0">
                  <div className="truncate font-medium">{l.designation}</div>
                  <div className="text-xs text-slate-400">{eur(l.unit_price_ht)}</div>
                </div>
                <Select value={l._cat} onValueChange={(v) => setPromptCat(i, v)}>
                  <SelectTrigger data-testid={`memorize-cat-${i}`} className="h-9 w-44 shrink-0"><SelectValue /></SelectTrigger>
                  <SelectContent>{MEMORIZE_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            ))}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="skip-save-library">Ne pas enregistrer</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-save-library" onClick={saveNewLinesToLibrary} className="bg-[#16A34A] hover:bg-[#15803D]">Enregistrer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
